<template>
  <div class="ta-dropdown" ref="rootRef" @keydown="onKeydown" @focusout="onFocusout">
    <button
      ref="triggerRef"
      type="button"
      class="ta-dropdown-btn"
      :aria-expanded="open"
      aria-haspopup="listbox"
      :aria-controls="popupId"
      :aria-labelledby="labelledby ? `${labelledby} ${valueId}` : undefined"
      :aria-label="labelledby ? undefined : `${ariaLabel} ${currentLabel}`"
      @click="toggle"
    >
      <span :id="valueId">{{ currentLabel }}</span>
      <svg viewBox="0 0 16 16" aria-hidden="true"><path d="m4 6 4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
    </button>
    <div v-if="open" :id="popupId" ref="menuRef" class="ta-dropdown-menu" role="listbox" :aria-label="ariaLabel" :aria-labelledby="labelledby">
      <button
        v-for="(option, index) in options"
        :key="`${typeof option.value}:${option.value}`"
        type="button"
        role="option"
        :aria-selected="modelValue === option.value"
        :tabindex="activeIndex === index ? 0 : -1"
        class="ta-dropdown-option"
        :class="{ 'ta-dropdown-on': modelValue === option.value }"
        @focus="activeIndex = index"
        @mousedown.prevent
        @click="select(option.value)"
      >
        <span>{{ option.label }}</span>
        <span v-if="option.hint" class="ta-dropdown-until">{{ option.hint }}</span>
        <svg v-if="modelValue === option.value" viewBox="0 0 16 16" aria-hidden="true"><path d="M13.3 4.3 6.4 11.2 2.7 7.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useId } from "vue";
import { moveDropdownFocus } from "~/utils/tool-dropdown.mjs";

// Custom dropdown in the ToolDesignA style — used for every date-like or
// choice input on the free-tool pages (never a native <select> or
// <input type="date">). Options: [{ value, label, hint? }].
const props = defineProps({
  modelValue: { type: [String, Number], default: "" },
  options: { type: Array, required: true },
  ariaLabel: { type: String, required: true },
  labelledby: { type: String, default: undefined },
});

const emit = defineEmits(["update:modelValue"]);

const open = ref(false);
const rootRef = ref(null);
const triggerRef = ref(null);
const menuRef = ref(null);
const activeIndex = ref(-1);
const id = useId();
const popupId = `tool-dropdown-${id}-listbox`;
const valueId = `tool-dropdown-${id}-value`;

const currentLabel = computed(() => {
  const match = props.options.find((option) => option.value === props.modelValue);
  return match ? match.label : props.options[0]?.label || "";
});

const focusOption = (index, key) => {
  activeIndex.value = moveDropdownFocus(
    menuRef.value?.querySelectorAll('[role="option"]') || [], index, key,
  );
};

const openMenu = async (key) => {
  activeIndex.value = Math.max(0, props.options.findIndex((option) => option.value === props.modelValue));
  open.value = true;
  await nextTick();
  if (open.value) focusOption(activeIndex.value, key);
};

const close = (restoreFocus = false) => {
  open.value = false;
  if (restoreFocus) triggerRef.value?.focus();
};

const toggle = () => {
  if (open.value) close();
  else openMenu();
};

const select = (value) => {
  emit("update:modelValue", value);
  close(true);
};

const onKeydown = (event) => {
  if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
    event.preventDefault();
    if (open.value) focusOption(activeIndex.value, event.key);
    else openMenu(["Home", "End"].includes(event.key) ? event.key : undefined);
  } else if (open.value && event.key === "Escape") {
    event.preventDefault();
    event.stopPropagation();
    close(true);
  } else if (open.value && ["Enter", " "].includes(event.key) && menuRef.value?.contains(event.target)) {
    event.preventDefault();
    const option = props.options[activeIndex.value];
    if (option) select(option.value);
  }
};

const onFocusout = (event) => {
  if (!rootRef.value?.contains(event.relatedTarget)) close();
};

const onDocClick = (event) => {
  if (rootRef.value && !rootRef.value.contains(event.target)) close();
};

onMounted(() => document.addEventListener("click", onDocClick));
onBeforeUnmount(() => document.removeEventListener("click", onDocClick));
</script>

<style scoped>
.ta-dropdown {
  position: relative;
}

.ta-dropdown-btn {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
  width: 100%;
  border: 1.5px solid #dde1e6;
  border-radius: 0.6rem;
  background: #fff;
  padding: 0.68rem 0.8rem;
  color: #111827;
  font-size: 0.93rem;
  font-weight: 700;
  cursor: pointer;
  transition: border-color 150ms ease, box-shadow 150ms ease;
}

.ta-dropdown-btn:hover { border-color: #9aa3ad; }

.ta-dropdown-btn[aria-expanded="true"] {
  border-color: #0e7490;
  box-shadow: 0 0 0 3.5px rgb(14 116 144 / 0.11);
}

.ta-dropdown-btn svg {
  width: 1rem;
  height: 1rem;
  color: #9aa3ad;
  transition: transform 150ms ease;
}

.ta-dropdown-btn[aria-expanded="true"] svg { transform: rotate(180deg); color: #0e7490; }

.ta-dropdown-menu {
  position: absolute;
  inset-inline: 0;
  top: calc(100% + 0.45rem);
  z-index: 30;
  overflow: hidden;
  border: 1px solid #e8eaee;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 18px 44px -10px rgb(15 23 42 / 0.18);
}

.ta-dropdown-option {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  width: 100%;
  border: none;
  background: #fff;
  padding: 0.68rem 0.85rem;
  color: #111827;
  font-size: 0.9rem;
  font-weight: 700;
  text-align: left;
  cursor: pointer;
  transition: background-color 120ms ease;
}

.ta-dropdown-option + .ta-dropdown-option {
  border-top: 1px solid #f1f2f4;
}

.ta-dropdown-option:hover { background: #f6f9fa; }

.ta-dropdown-btn:focus-visible,
.ta-dropdown-option:focus-visible {
  outline: 2px solid #0e7490;
  outline-offset: -3px;
}

.ta-dropdown-until {
  color: #9aa3ad;
  font-size: 0.78rem;
  font-weight: 600;
}

.ta-dropdown-option svg {
  width: 0.95rem;
  height: 0.95rem;
  margin-left: auto;
  color: #0e7490;
}

.ta-dropdown-on { color: #0e7490; }
.ta-dropdown-on .ta-dropdown-until { color: #0e7490; }

/* Dark mode: the site's dark tokens (page #020617, card #0f172a, hairline #243247, text #f1f5f9 / #cbd5e1 / #94a3b8, accent #67e8f9). Light styles above are unchanged. */
.dark .ta-dropdown-btn {
  border: 1.5px solid #243247;
  background: #0f172a;
  color: #f1f5f9;
}

.dark .ta-dropdown-btn svg {
  color: #94a3b8;
}

.dark .ta-dropdown-btn[aria-expanded="true"] svg {
  color: #67e8f9;
}

.dark .ta-dropdown-menu {
  border: 1px solid #243247;
  background: #0f172a;
}

.dark .ta-dropdown-option {
  background: #0f172a;
  color: #f1f5f9;
}

.dark .ta-dropdown-option + .ta-dropdown-option {
  border-top: 1px solid #243247;
}

.dark .ta-dropdown-option:hover {
  background: rgb(14 116 144 / 0.16);
}

.dark .ta-dropdown-btn:focus-visible,
.dark .ta-dropdown-option:focus-visible {
  outline-color: #67e8f9;
}

.dark .ta-dropdown-until {
  color: #94a3b8;
}

.dark .ta-dropdown-option svg {
  color: #67e8f9;
}

.dark .ta-dropdown-on {
  color: #67e8f9;
}

.dark .ta-dropdown-on .ta-dropdown-until {
  color: #67e8f9;
}

</style>
